/**
 * Intl-based currency formatting helpers and shared formatting context.
 */

import { normalizeLocaleTag } from '../dates';

const currencyContext = {
    locale: undefined,
    defaultCurrencyISO: null,
    defaultUserCurrencySymbol: null,
};

/**
 * @param {{ locale?: string|null, defaultCurrencyISO?: string|null, defaultUserCurrencySymbol?: string|null }} context
 */
export const setCurrencyFormattingContext = (context = {}) => {
    if ('locale' in context) {
        currencyContext.locale = context.locale ?? undefined;
    }

    if ('defaultCurrencyISO' in context) {
        currencyContext.defaultCurrencyISO = context.defaultCurrencyISO ?? null;
    }

    if ('defaultUserCurrencySymbol' in context) {
        currencyContext.defaultUserCurrencySymbol = context.defaultUserCurrencySymbol ?? null;
    }
};

export const resetCurrencyFormattingContext = () => {
    currencyContext.locale = undefined;
    currencyContext.defaultCurrencyISO = null;
    currencyContext.defaultUserCurrencySymbol = null;
};

export const getCurrencyFormattingContext = () => ({ ...currencyContext });

/**
 * Formats a monetary amount with `Intl.NumberFormat`.
 * Amount is expressed in minor units and divided by 10^precision before formatting.
 *
 * @param {{ amount: number, precision: number, currency: string, locale?: string|null }} params
 * @returns {string}
 */
export const formatCurrencyAmount = ({
    amount,
    precision,
    currency,
    locale = currencyContext.locale,
}) => {
    const value = amount / Math.pow(10, precision);
    const options = { style: 'currency', currency };
    const resolvedLocale = locale ?? currencyContext.locale;

    if (resolvedLocale == null) {
        return new Intl.NumberFormat(options).format(value);
    }

    return new Intl.NumberFormat(
        normalizeLocaleTag(resolvedLocale) || resolvedLocale,
        options,
    ).format(value);
};

/**
 * @param {string} formatted
 * @param {string} symbol
 * @returns {string}
 */
export const addSpaceAfterCurrencySymbol = (formatted, symbol) => {
    if (!symbol || formatted.indexOf(symbol) === -1) {
        return formatted;
    }

    return formatted.replace(symbol, `${symbol} `);
};

/**
 * @param {string} formatted
 * @param {string} symbol
 * @returns {string}
 */
export const replaceCurrencyGlyph = (formatted, symbol) => {
    let result = formatted;

    if (symbol) {
        result = result.replace('€', symbol);
        result = result.replace('EUR', symbol);
    }

    return result;
};

/**
 * @param {string} currencyCode
 * @param {string} [locale]
 * @returns {string}
 */
export const formatCurrencySample = (currencyCode, locale = currencyContext.locale) => {
    return formatCurrencyAmount({
        amount: 99,
        precision: 0,
        currency: currencyCode,
        locale,
    });
};

/**
 * @param {string} currencyCode
 * @param {string} [locale]
 * @returns {'before'|'after'}
 */
export const getCurrencySymbolPositionFromSample = (
    currencyCode = 'EUR',
    locale = currencyContext.locale,
) => {
    const formatted = formatCurrencySample(currencyCode, locale);
    const firstDigitIndex = formatted.search(/\d/);

    if (firstDigitIndex <= 0) {
        return 'after';
    }

    return 'before';
};
